import { enqueueSnackbar } from 'notistack'
import { AxiosResponse } from 'axios'
import { axiosInstance, axiosInstanceEntity } from './axiosInstance'
import {
    AttributeType,
    BUSINESS_RULES_FIELD_ID,
    EntityStatus,
    EntityStepTypes,
} from '../constants/constants'
import { getChartAPIPayloadDataSets } from '../utils/EntityAttributesUtils'
import { downloadFileFromResponse } from '../utils/FileUtils'
import { IAttribute, IAttribute2 } from '../components/DTStudio/interfaces'
import {
    ISiteBuildingAssetCount,
    OrgColumnFilterAPIRequest,
    OrgColumnFilterAPIResponse,
    TemplateSubTypesAPIRequest,
    TemplateSubTypesAPIResponse,
} from '../components/Entity/interfaces'
import { ISelectedAttributes } from '../components/ViewEntity/interfaces'
import { AlertsApiResponse } from '../components/ViewEntity/TabsView/AlertsView/interfaces'
import {
    IEntityInstanceRelationsResponse,
    IMapAPIResponse,
} from './apiInterfaces'

// Type definitions
interface ToasterResult {
    data?: {
        displayMessage?: string
        message?: string
    }
    response?: {
        data?: {
            displayMessage?: string
            message?: string
        }
    }
}

interface EntityStepAttribute {
    id: string
    value: any
    dataType: string
    [key: string]: any
}

interface EntityStep {
    stepType: string
    attributes: EntityStepAttribute[]
}

export interface EntitySaveResponse {
    type: 'success' | 'error'
    payload: any
}

export interface ColumnsFilterInPayLoad {
    attributeId: string
    values?: string | string[]
    fromDate?: string
    toDate?: string
}

export interface FetchEntityRecordsParams {
    searchText?: string
    filters?: ColumnsFilterInPayLoad[]
    filterBy?: EntityStatus[] | string //EntityStataus for entity API call, string for records API call
    hasActiveRelation?: boolean | null
    roleBasedFetchAll?: boolean
}

export interface FetchShipmentEntityRecordsParams
    extends FetchEntityRecordsParams {
    pageNo: number
    pageSize: number
    shipmentStatusFilter: string
    alarmStatusFilter: string
    searchAttributeName: string
    searchAttributeType: string
    dateFilterAttributeId: string
    fromDate: string
    toDate: string
    currentLocationBounds: any
    orderAttribute: string
    orderBy: string
    entitySearch: boolean
    includeParentRelations?: boolean
}

export interface ExportShipmentEntityRecordsParams
    extends Omit<FetchShipmentEntityRecordsParams, 'pageNo' | 'pageSize'> {
    timezone: string
}

interface ChartPayload {
    chartType: string
    dataSets: any[]
    mainEntityId: string
    startTime: string
    endTime: string
    dataType: string
    clampToRelationStart?: boolean
}

interface UsersParams {
    [key: string]: any
}

interface UserActionPayload {
    [key: string]: any
}

interface RelationPayload {
    [key: string]: any
}

interface ColumnsSelectionsPayload {
    [key: string]: any
}

export interface ExportParams {
    searchText?: string
    [key: string]: any
}

export interface IFetchTimeSeriesTableParams {
    viewID: string
    selectedAttributes: ISelectedAttributes[]
    startDate: string
    endDate: string
    orgId: string
    pageNumber: number
    pageSize: number
    filterOutlierLocations?: boolean
    isGuestUser?: boolean
    filterOutNonTimeSeriesData?: boolean
    sortBy?: 'ASC' | 'DESC'
    guestUserPreferredUnit?: string
    includeStage?: boolean
    filterLightTriggered?: boolean
    filterShockTriggered?: boolean
    clampToRelationStart?: boolean
}
interface IExportTimeSeriesParams extends IFetchTimeSeriesTableParams {
    userTimeZone: string
    segment: string
}

export interface IInsuranceData {
    actions: string[]
    attributes: IAttribute2[]
    isInsuranceCreated: boolean
    insuranceInstanceId: string
    insuranceEntityTemplateId: string
}

export interface IPostInsuranceActionData {
    shipmentEntityId: string
    action: string //'generate', 'Approve', 'Reject', 'Update'
}

export const showToasterMessage = (
    result: ToasterResult,
    variant: 'success' | 'error',
): void => {
    if (variant === 'success') {
        enqueueSnackbar(
            result?.data?.displayMessage || result?.data?.message || '',
            {
                variant: 'success',
            },
        )
    } else {
        enqueueSnackbar(
            result?.response?.data?.displayMessage ||
                result?.response?.data?.message ||
                'error',
            {
                variant: 'error',
            },
        )
    }
}

export const fetchBusinessRules = async (
    orgId: string,
    entityId: string,
): Promise<any> => {
    return await axiosInstanceEntity
        .get(`/rule/v2/${orgId}?entityTemplateId=${entityId}`)
        .then((response: AxiosResponse) => {
            return response.data.data
        })
        .catch((err: any) => {
            return err.response
        })
}

/**
 * TODO: This method is already implemented in a custom hook. useGetAttributes.
 * Replace this method with that hook.
 */
export const fetchAttributesByTemplateId = async (
    orgId: string,
    entityTemplateId: string,
    excludeDynamicAttributes: boolean = false,
    excludeHiddenAttributes: boolean = false,
    includeChildAttributes: boolean = false,
    includeDynamicAttributesOnly: boolean = false,
): Promise<IAttribute[]> => {
    return await axiosInstanceEntity
        .get(
            `/attributes?orgId=${orgId}&entityTemplateId=${entityTemplateId}&excludeDynamicAttributes=${excludeDynamicAttributes}&excludeHiddenAttributes=${excludeHiddenAttributes}&includeChildAttributes=${includeChildAttributes}&includeDynamicAttributesOnly=${includeDynamicAttributesOnly}`,
        )
        .then((response: AxiosResponse) => {
            return response?.data?.attributeResponse
                ? response?.data?.attributeResponse
                : []
        })
        .catch((err: any) => {
            return err.response
        })
}

export const fetchStaticAttributes = async (
    orgId: string,
    entityTemplateId: string,
): Promise<IAttribute[]> => {
    return await axiosInstanceEntity
        .get(
            `/attributes?orgId=${orgId}&entityTemplateId=${entityTemplateId}&excludeDynamicAttributes=${true}`,
        )
        .then((response: AxiosResponse) => {
            return response?.data?.attributeResponse
                ? response?.data?.attributeResponse
                : []
        })
        .catch((err: any) => {
            return err.response
        })
}

export const fetchUsersForEntityInstance = async (
    params: UsersParams,
): Promise<AxiosResponse> => {
    return await axiosInstance
        .get(`/users`, { params })
        .then((response: AxiosResponse) => {
            return response
        })
        .catch((err: any) => {
            return err.response
        })
}

export const generatePDFReportForEntity = async (
    orgId: string,
    entityTemplateId: string,
    entityId: string,
    payload: unknown,
    loggedInOrgId: string,
    includeStage: boolean,
): Promise<any> => {
    const response = await axiosInstanceEntity.post(
        `entity/entity-report?orgId=${orgId}&templateId=${entityTemplateId}&entityId=${entityId}&loggedInOrgId=${loggedInOrgId}&includeStage=${includeStage}`,
        payload,
    )
    return response.data
}

export const onSaveEntityFields = async (
    sendData: EntityStep[],
    orgId: string,
    entityTemplateId: string,
    recordId: string,
    status: string,
    loggedInOrgId: string,
): Promise<EntitySaveResponse> => {
    //changing the send data struture due to schema changes in back end
    const copySendData = [...sendData]
    const updatedSendData = {
        attributeValues: {} as Record<string, any>,
        ruleSetId: null as string | null,
        relations: {} as Record<string, any>,
        status,
    }
    copySendData.forEach((item) => {
        if (item.stepType === EntityStepTypes.Attributes) {
            const attributeValues: Record<string, any> = {}
            item.attributes.forEach((i) => {
                if (
                    i.dataType.toLowerCase() === AttributeType.DEVICE_RELATION
                ) {
                    updatedSendData.relations[i.relationId] = i.value
                } else if (i.id === BUSINESS_RULES_FIELD_ID) {
                    updatedSendData.ruleSetId = i.value
                } else {
                    attributeValues[i.id] = i.value
                }
            })
            updatedSendData.attributeValues = attributeValues
        }
    })

    return await axiosInstanceEntity
        .post(
            `/entity?orgId=${orgId}&templateId=${entityTemplateId}&entityId=${recordId}&loggedInOrgId=${loggedInOrgId}`,
            updatedSendData,
        )
        .then((response: AxiosResponse): EntitySaveResponse => {
            showToasterMessage(response, 'success')
            return { type: 'success' as const, payload: response.data }
        })
        .catch((err: any): EntitySaveResponse => {
            showToasterMessage(err, 'error')

            return { type: 'error' as const, payload: err.response }
        })
}
// save/update entity instance with out steps(relations). saving with only attributes
export const onSaveEntityInstance = async (
    sendData: IAttribute[],
    orgId: string,
    entityTemplateId: string,
    recordId: string,
    status: string,
    loggedInOrgId: string,
): Promise<EntitySaveResponse> => {
    const updatedSendData = {
        attributeValues: {} as Record<string, any>,
        ruleSetId: null as string | null,
        relations: {} as Record<string, any>,
        status,
    } // if backend updated API to save entity instance with attributes only update the payload.(with out relations)
    sendData.forEach((i) => {
        updatedSendData.attributeValues[i.id] = i.value
    })

    return await axiosInstanceEntity
        .post(
            `/entity?orgId=${orgId}&templateId=${entityTemplateId}&entityId=${recordId}&loggedInOrgId=${loggedInOrgId}`,
            updatedSendData,
        )
        .then((response: AxiosResponse): EntitySaveResponse => {
            showToasterMessage(response, 'success')
            return { type: 'success' as const, payload: response.data }
        })
        .catch((err: any): EntitySaveResponse => {
            showToasterMessage(err, 'error')

            return { type: 'error' as const, payload: err.response }
        })
}

/**
 * @deprecated Use fetchEntityRecordsList instead
 */
export const fetchEntityRecords = async (
    orgId: string,
    entityId: string,
    params: FetchEntityRecordsParams,
): Promise<any> => {
    const encodedParams = {
        ...params,
        localTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }
    return await axiosInstanceEntity
        .get(`/entity/records?templateId=${entityId}&orgId=${orgId}`, {
            params: encodedParams,
        })
        .then((response: AxiosResponse) => {
            return response?.data
        })
        .catch(() => {
            return {}
        })
}

// new API for getting records list. it's for only listing page
export const fetchEntityRecordsList = async (
    orgId: string,
    entityId: string,
    params: FetchEntityRecordsParams,
): Promise<any> => {
    const encodedParams = {
        ...params,
        localTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }
    return await axiosInstanceEntity
        .post(`/entity/entities`, {
            templateId: entityId,
            orgId,
            ...encodedParams,
        })
        .then((response: AxiosResponse) => {
            return response?.data
        })
        .catch(() => {
            return {}
        })
}

export const fetchRecordDataByID = async (
    orgId: string,
    entityId: string,
    recordId: string,
): Promise<any> => {
    return await axiosInstanceEntity
        .get(
            `/entity/${recordId}/${entityId}?orgId=${orgId}&excludeDynamicAttributes=${true}`,
        )
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch((err: any) => {
            return err.response
        })
}

export const fetchRecordRelationDataByID = async (
    orgId: string,
    entityTemplateId: string,
    recordId: string,
    excludeDynamicAttributes: boolean = true,
    isGuestUser: boolean = false,
    guestUserPreferredUnit?: string,
): Promise<IEntityInstanceRelationsResponse> => {
    const path = isGuestUser
        ? `/coldChain/public/relation/${recordId}/${entityTemplateId}`
        : `/entity/relation/${recordId}/${entityTemplateId}`
    return await axiosInstanceEntity
        .get(path, {
            params: {
                orgId,
                excludeDynamicAttributes,
                preferredTempUnit: guestUserPreferredUnit,
            },
            isGuestUser,
        } as any)
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch((err: any) => {
            return err.response
        })
}

/**
 *
 * @param orgId
 * @param entityTemplateId
 * @param recordIds
 * @returns For a given entityTemplateId and it's instances in recordIds(entityInstanceIds), this API will give
 * all the relations of the recordIds.
 */

export const fetchMultiRecordRelationsByArrayOfIDs = async (
    orgId: string,
    entityTemplateId: string,
    recordIds: string,
    excludeDynamicAttributes: boolean = true,
): Promise<any> => {
    return await axiosInstanceEntity
        .get(`/entity/relations`, {
            params: {
                orgId,
                entities: recordIds,
                entityTemplateId,
                excludeDynamicAttributes,
            },
        })
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch((err: any) => {
            return err.response
        })
}

export const fetchRecordDataByIdForPreview = async (
    orgId: string,
    recordId: string,
    isGuestUser: boolean = false,
    guestUserPreferredUnit?: string,
): Promise<any> => {
    const path = isGuestUser ? '/coldChain/public/preview' : `/entity/preview`
    return axiosInstanceEntity
        .get(path, {
            params: {
                orgId,
                recordId,
                localTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                preferredTempUnit: guestUserPreferredUnit,
            },
            isGuestUser,
        } as any)
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch(() => {
            return {}
        })
}

export const fetchAlerts = async (
    params: unknown,
    isGuestUser: boolean = false,
    guestUserPreferredUnit?: string,
): Promise<AlertsApiResponse> => {
    const path = isGuestUser ? '/coldChain/public/alerts' : `/alerts`
    return axiosInstanceEntity
        .get(path, {
            params: {
                ...(params as Record<string, unknown>),
                localTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                preferredTempUnit: guestUserPreferredUnit,
            }, // Query parameters
            isGuestUser,
        } as any)
        .then((response: AxiosResponse) => response.data)
        .catch((err: any) => err.response)
}

/**
 * Fetch one page of selectable entities for a relation.
 *
 * `pageNo` is 1-based and is sent on the wire as `start`, which is the name
 * `/entity/selector` actually binds. UBQ-5573: this was previously sent as
 * `pageNo`, a name the endpoint does not read — Spring discarded it, `start`
 * stayed at its default of 1, and every request returned the first page.
 */
export const fetchEntityRecordsListBySearch = async (
    orgId: string,
    relationTemplateId: string,
    parentTemplateId: string,
    searchText: string,
    includeAttributes: boolean = false,
    pageNo: number = 1,
    pageSize: number = 10000,
): Promise<any> => {
    return axiosInstanceEntity
        .get(`/entity/selector`, {
            params: {
                relationTemplateId,
                parentTemplateId,
                searchText,
                orgId,
                includeAttributes,
                start: pageNo,
                pageSize,
            },
        })
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch((err: any) => {
            return err.response
        })
}

export const fetchEntityColumns = async (entityId: string): Promise<any> => {
    return await axiosInstanceEntity
        .get(`/entity/searchableAttributes?templateId=${entityId}`)
        .then((response: AxiosResponse) => {
            return response.data.data
        })
        .catch((err: any) => {
            return err.response
        })
}

export const fetchIsUniqueUID = async (UID: string): Promise<any> => {
    return await axiosInstanceEntity
        .get(`/entity/${UID}`)
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch((err: any) => {
            return err.response
        })
}

export const updateEntityInstancesStatus = async (
    orgId: string,
    entityIds: (string | number)[],
    status: string,
): Promise<EntitySaveResponse> => {
    return await axiosInstanceEntity
        .put(`/entity?orgId=${orgId}&status=${status}`, {
            entityInstanceIds: entityIds,
        })
        .then((response: AxiosResponse) => {
            showToasterMessage(response, 'success')
            return { type: 'success' as const, payload: response }
        })
        .catch((err: any) => {
            showToasterMessage(err, 'error')
            return { type: 'error' as const, payload: err.response }
        })
}

export const fetchMapData = async (
    viewID: string,
    selectedAttributes: ISelectedAttributes[],
    startDate: string,
    endDate: string,
    orgId: string,
    filterOutlierLocations: boolean,
    includeDeviceAttributesForShipment: boolean,
    includeHistoricData: boolean,
    isGuestUser: boolean = false,
    guestUserPreferredUnit?: string,
): Promise<IMapAPIResponse> => {
    const path = isGuestUser ? '/coldChain/public/map' : `/chart/map`
    const chartAPIPayloadDataSets = getChartAPIPayloadDataSets(
        selectedAttributes,
        true,
    )
    const chartPayload: ChartPayload = {
        chartType: '',
        dataSets: chartAPIPayloadDataSets,
        mainEntityId: viewID,
        startTime: startDate,
        endTime: endDate,
        dataType: chartAPIPayloadDataSets[0]?.dataType || '', //for temporary perpose
    }
    return await axiosInstanceEntity
        .post(path, chartPayload, {
            params: {
                filterOutlierLocations,
                orgId,
                includeDeviceAttributesForShipment,
                includeHistoricData,
                preferredTempUnit: guestUserPreferredUnit,
            },
            isGuestUser,
        } as any)
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch(() => {
            return { dataSets: [] }
        })
}

export const fetchApacheEChartData = async (
    viewID: string,
    selectedAttributes: ISelectedAttributes[],
    startDate: string,
    endDate: string,
    orgId: string,
    isGuestUser: boolean = false,
    guestUserPreferredUnit?: string,
    clampToRelationStart?: boolean,
): Promise<any> => {
    const path = isGuestUser ? '/coldChain/public/chart/v2' : '/chart/v2'
    const chartAPIPayloadDataSets = getChartAPIPayloadDataSets(
        selectedAttributes,
        true,
    )
    const chartPayload: ChartPayload = {
        chartType: '',
        dataSets: chartAPIPayloadDataSets,
        mainEntityId: viewID,
        startTime: startDate,
        endTime: endDate,
        dataType: chartAPIPayloadDataSets[0]?.dataType || '', //for temporary perpose
        clampToRelationStart,
    }
    return await axiosInstanceEntity
        .post(path, chartPayload, {
            params: {
                orgId,
                preferredTempUnit: guestUserPreferredUnit,
            },
            isGuestUser,
        } as any)
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch((err: any) => {
            return err.response
        })
}

export const fetchTimeseriesTableData = async ({
    viewID,
    selectedAttributes,
    startDate,
    endDate,
    orgId,
    pageNumber,
    pageSize,
    filterOutlierLocations = false,
    isGuestUser = false,
    filterOutNonTimeSeriesData = true,
    sortBy = 'ASC',
    guestUserPreferredUnit,
    includeStage,
    filterLightTriggered = false,
    filterShockTriggered = false,
    clampToRelationStart,
}: IFetchTimeSeriesTableParams): Promise<any> => {
    const path = isGuestUser ? '/coldChain/public/table' : '/chart/table'
    const chartAPIPayloadDataSets = getChartAPIPayloadDataSets(
        selectedAttributes,
        filterOutNonTimeSeriesData,
    )
    const chartPayload: ChartPayload = {
        chartType: '',
        dataSets: chartAPIPayloadDataSets,
        mainEntityId: viewID,
        startTime: startDate,
        endTime: endDate,
        dataType: chartAPIPayloadDataSets[0]?.dataType || '', //for temporary perpose
        clampToRelationStart,
    }
    return axiosInstanceEntity
        .post(path, chartPayload, {
            params: {
                filterOutlierLocations,
                orgId,
                pageNumber,
                pageSize,
                sortBy,
                preferredTempUnit: guestUserPreferredUnit,
                includeStage,
                filterLightTriggered,
                filterShockTriggered,
            },
            isGuestUser,
        } as any)
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch(() => {
            return {}
        })
}

export const uploadEntityInstanceImage = async (
    organizationName: string,
    entityFile: FormData,
): Promise<EntitySaveResponse> => {
    return await axiosInstanceEntity
        .post(
            `/entity/entityImage?organizationName=${organizationName}`,
            entityFile,
        )
        .then((response: AxiosResponse) => {
            return { type: 'success' as const, payload: response.data }
        })
        .catch((err: any) => {
            showToasterMessage(err, 'error')
            return { type: 'error' as const, payload: err.response }
        })
}

export const updateEntityInstance = async (payload: unknown): Promise<any> => {
    return await axiosInstanceEntity
        .put(`/entity/facilityMonitoring`, payload)
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch((err: any) => {
            throw err
        })
}

export const updateEntityInstanceByDeletionOfProperties = async (
    payload: unknown,
): Promise<any> => {
    return await axiosInstanceEntity
        .post(`/entity/delete/facilityMonitoring`, payload)
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch((err: any) => {
            throw err
        })
}

export const postUserAction = async (
    payload: UserActionPayload,
): Promise<any> => {
    return await axiosInstanceEntity
        .post(`/entity/action`, payload)
        .then((response: AxiosResponse) => {
            showToasterMessage(response, 'success')
            return response.data
        })
        .catch((err: any) => {
            showToasterMessage(err, 'error')
            return err.response
        })
}

export const postInsuranceAction = async (
    data: IPostInsuranceActionData,
    orgId: string,
): Promise<void> => {
    return axiosInstanceEntity
        .post('/cargocover/generate-quote', data, {
            params: { orgId },
        })
        .then((res: AxiosResponse) => {
            showToasterMessage(res, 'success')
            return res.data
        })
        .catch((err) => showToasterMessage(err, 'error'))
}

export const getAvailbleInsuranceActions = async (
    recordId: string,
    orgId: string,
    isGuestUser: boolean = false,
): Promise<IInsuranceData> => {
    const path = isGuestUser
        ? '/coldChain/public/get-actions'
        : '/cargocover/get-actions'
    return await axiosInstanceEntity
        .get(path, {
            params: {
                orgId,
                recordId,
                localTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            isGuestUser,
        } as any)
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch(() => {
            return {}
        })
}

/**
 * @deprecated This function is deprecated and will be removed in a future version.
 */
export const getSubTreeOfEntities = async (payload: {
    templateId: string
    levels: number
}): Promise<any> => {
    return await axiosInstanceEntity
        .get(
            `/entity/childEntities?templateId=${payload.templateId}&levels=${payload.levels}`,
        )
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch(() => {
            return {}
        })
}

export const getBuildingsAssetsCount = async (
    orgId: string,
    includeParentTreePath = true,
): Promise<ISiteBuildingAssetCount[]> => {
    return await axiosInstanceEntity
        .get(
            `/entity/childEntities/v2?orgId=${orgId}&includeParentTreePath=${includeParentTreePath}`,
        )
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch(() => {
            return []
        })
}

export const modifingItemInRelation = async (
    orgId: string,
    payload: RelationPayload,
    showToastMessage: boolean = true,
): Promise<EntitySaveResponse> => {
    return await axiosInstanceEntity
        .put(`/entity/relation?orgId=${orgId}`, payload)
        .then((response: AxiosResponse) => {
            if (showToastMessage) {
                showToasterMessage(response, 'success')
            }
            return { type: 'success' as const, payload: response.data }
        })
        .catch((err: any) => {
            showToasterMessage(err, 'error')
            return { type: 'error' as const, payload: err.response }
        })
}

export const fetchAllUserUnits = async (): Promise<any[]> => {
    return await axiosInstanceEntity
        .get(`/settings/units`)
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch(() => {
            return []
        })
}

export const fetchColumnsSelections = async (
    templateId: string,
    orgId: string,
    includeParentRelations: boolean = false,
): Promise<any[]> => {
    return await axiosInstanceEntity
        .get(
            `/entity/org/selectedColumns?templateId=${templateId}&orgId=${orgId}&includeParentRelations=${includeParentRelations}`,
        )
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch((err: any) => {
            showToasterMessage(err, 'error')
            return []
        })
}

export const postColumnsSelections = async (
    templateId: string,
    orgId: string,
    payload: ColumnsSelectionsPayload,
): Promise<any> => {
    return await axiosInstanceEntity
        .put(
            `/entity/org/selectedColumns?templateId=${templateId}&orgId=${orgId}`,
            payload,
        )
        .then((response: AxiosResponse) => {
            showToasterMessage(response, 'success')
            return response.data
        })
        .catch((err: any) => {
            showToasterMessage(err, 'error')
            return err.response
        })
}

export const fetchStateList = async (countryCode: string): Promise<any[]> => {
    return await axiosInstanceEntity
        .get(`/api/states?countryCode=${countryCode}`)
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch(() => {
            return []
        })
}

export const exportEntityRecordsAsXLS = async (
    templateId: string,
    orgId: string,
    params: ExportParams,
    selectedColumns: any[],
): Promise<void> => {
    await axiosInstanceEntity
        .post(
            `/entity/export`,
            {
                ...params,
                templateId,
                orgId,
                selectedColumns,
            },
            {
                responseType: 'blob',
            },
        )
        .then((response: AxiosResponse) => {
            downloadFileFromResponse(response, 'export.xlsx')
        })
        .catch((err: any) => {
            showToasterMessage(err, 'error')
        })
}

export const exportEntityRecordsAsTimeSeriesXLS = async ({
    viewID,
    selectedAttributes,
    startDate,
    endDate,
    orgId,
    pageNumber,
    pageSize,
    filterOutlierLocations = false,
    isGuestUser = false,
    filterOutNonTimeSeriesData = true,
    userTimeZone,
    sortBy = 'ASC',
    guestUserPreferredUnit,
    segment,
    includeStage,
    filterLightTriggered = false,
    filterShockTriggered = false,
}: IExportTimeSeriesParams): Promise<void> => {
    const path = isGuestUser
        ? '/coldChain/public/export/timeseries'
        : '/entity/export/timeseries'
    const chartAPIPayloadDataSets = getChartAPIPayloadDataSets(
        selectedAttributes,
        filterOutNonTimeSeriesData,
    )
    const chartPayload: ChartPayload = {
        chartType: '',
        dataSets: chartAPIPayloadDataSets,
        mainEntityId: viewID,
        startTime: startDate,
        endTime: endDate,
        dataType: chartAPIPayloadDataSets[0]?.dataType || '',
    }

    await axiosInstanceEntity
        .post(path, chartPayload, {
            params: {
                filterOutlierLocations,
                orgId,
                pageNumber,
                pageSize,
                timeZone: userTimeZone,
                sortBy,
                preferredTempUnit: guestUserPreferredUnit,
                segment,
                includeStage,
                filterLightTriggered,
                filterShockTriggered,
            },
            responseType: 'blob',
            isGuestUser,
        } as any)
        .then((response: AxiosResponse) => {
            downloadFileFromResponse(response, 'export.xlsx')
        })
        .catch((err: any) => {
            showToasterMessage(err, 'error')
        })
}

export const fetchCloumnsOrderForPreview = async (
    templateId: string,
    orgId: string,
    isGuestUser: boolean,
): Promise<any[]> => {
    const path = isGuestUser
        ? `/coldChain/public/previewColumns?templateId=${templateId}&orgId=${orgId}`
        : `/entity/org/previewColumns?templateId=${templateId}&orgId=${orgId}`

    return await axiosInstanceEntity
        .get(path, {
            isGuestUser,
        } as any)
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch((err: any) => {
            showToasterMessage(err, 'error')
            return []
        })
}

export const postCloumnsOrderForPreview = async (
    templateId: string,
    orgId: string,
    payload: ColumnsSelectionsPayload,
    isGuestUser: boolean,
): Promise<any> => {
    const path = isGuestUser
        ? `/coldChain/public/previewColumns?templateId=${templateId}&orgId=${orgId}`
        : `/entity/org/previewColumns?templateId=${templateId}&orgId=${orgId}`
    return await axiosInstanceEntity
        .put(path, payload)
        .then((response: AxiosResponse) => {
            showToasterMessage(response, 'success')
            return response.data
        })
        .catch((err: any) => {
            showToasterMessage(err, 'error')
            return err.response
        })
}

export const fetchDisplayableLabels = async (
    orgId: string,
    recordIds: string[],
): Promise<any> => {
    return await axiosInstanceEntity
        .post(`/entity/displayableIds?orgId=${orgId}`, {
            entities: recordIds,
        })
        .then((response: AxiosResponse) => {
            return response.data || []
        })
        .catch(() => {
            return []
        })
}
/**
 *
 * @param orgId
 * @param templateId
 * @returns This API gives a list organizations. The current logged in user has some entities shared
 * from these organizations for a given templateId
 */

export const getOrganizationsOfEntities = async (
    requestParams: OrgColumnFilterAPIRequest,
): Promise<OrgColumnFilterAPIResponse> => {
    return axiosInstanceEntity
        .get('entity/org/filter', {
            params: requestParams,
        })
        .then((response: AxiosResponse) => {
            return response.data
        })
        .catch(() => {
            return []
        })
}

export const getTemplateSubTypes = async (
    requestParams: TemplateSubTypesAPIRequest,
): Promise<TemplateSubTypesAPIResponse[]> => {
    return axiosInstanceEntity
        .get('entity/org/templateSubTypes', {
            params: requestParams,
        })
        .then((response: AxiosResponse) => {
            return response.data?.data
        })
        .catch(() => {
            return []
        })
}

export const postTransferEntitiesToOrgs = async (
    orgId: string,
    entityIds: string[],
): Promise<any> => {
    return axiosInstanceEntity
        .put('/entity/transfer', {
            entityIds,
            orgId,
        })
        .then((response: AxiosResponse) => {
            showToasterMessage(response, 'success')
            return response
        })
        .catch((err: any) => {
            showToasterMessage(err, 'error')
            return err.response
        })
}
